/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */


import { useEffect, useMemo, useState } from 'react';
import Loader from '../../components/loader';
import Dashboard, { DashboardRow, DashboardSection, DashboardSectionTitle } from '../../components/dashboard';
import { DATE, TIME_RANGE_OPTIONS, formatDate } from '../../utils/date';
import { useAppDispatch, useAppSelector } from '../../reducers';
import { useParams } from 'react-router';
import { AdminBreadcrumbs } from './AdminBreadcrumbs';
import { Button, Progress } from 'reactstrap';
import Table, { TableColumn } from '../../components/table/Table';
import { getPeriodName } from '../../utils/universalTracker';
import { Link, Redirect, useHistory, useRouteMatch } from 'react-router-dom';
import { AdminUserTable } from './components/AdminUserTable';
import { initiativeApi, StatusStats, SurveyStats, useGetStatsQuery, useGetUserStatsQuery, UserWithDelegation } from '../../api/initiative-stats';
import { loadInitiativeById } from '../../actions/initiative';
import { naturalSort } from '../../utils';
import { ProgressRow } from './ProgressRow';
import { PAGE_SIZE, getUTCDateRange, getUserDelegationStats, toPercentageProgress } from './util';
import { BulkSurveysMessageModal, BulkUsersMessageModal, SurveyMessageModal } from '../../components/message-modal/MessageModal';
import { CompleteButton } from '../../components/survey/button/CompleteButton';
import { isUserManagerByInitiativeId } from '../../selectors/user';
import G17Client from '../../services/G17Client';
import { Action, SurveysEditing } from './components/SurveysEditing/SurveysEditing';
import { BasicAlert } from '../../components/alert/BasicAlert';
import { PeriodDropdown } from '../../components/period-dropdown';
import { DateRangeType, getDateRange } from '../../components/period-dropdown/DateRangePicker';
import { generateUrl } from '../util';
import { ROUTES } from '../../constants/routes';
import { isSingleOrg } from '../../selectors/initiative';
import { Toggle } from '../../components/common/Toggle';
import { InitiativePermissions } from '../../services/permissions/InitiativePermissions';
import { QuestionManagement } from './components/questions/QuestionManagement';
import { QUESTION } from '../../constants/terminology';
import { FeaturePermissions } from '@services/permissions/FeaturePermissions';

interface RouteParams {
  initiativeId: string,
}

enum Filter {
  Pending = 'pending',
  Completed = 'completed',
}

export enum UserFilter {
  Explicit = 'explicit',
  Permission = 'permission',
}

export const AdminOverview = () => {
  const { url } = useRouteMatch();
  const history = useHistory();
  const dispatch = useAppDispatch();

  const { initiativeId } = useParams<RouteParams>();
  const [filter, setFilter] = useState<Filter>(Filter.Pending);
  const [userFilter, setUserFilter] = useState<UserFilter>(UserFilter.Explicit);

  const [showMessageModal, setShowMessageModal] = useState<string | undefined>(undefined);
  const [showBulkSurveysMessage, setShowBulkSurveysMessage] = useState<boolean>(false);
  const [showBulkUsersMessage, setShowBulkUsersMessage] = useState<boolean>(false);

  const limitReportingLevels = useAppSelector(FeaturePermissions.getLimitReportingLevels);
  const isSingleOrganisation = useAppSelector(isSingleOrg);
  const initiativeState = useAppSelector((state) => state.initiative);
  const [dateRange, setDateRange] = useState<DateRangeType>(getDateRange(12));
  const query = useMemo(() => new URLSearchParams(history.location.search), [history.location.search])
  const userId = query.get('userId');

  const isCompleteFilter = filter === Filter.Completed;
  const isPermissionFilter = userFilter === UserFilter.Permission;
  const isUserAdmin = useAppSelector((state) => isUserManagerByInitiativeId(state, initiativeId));

  const { isLoading, isFetching, isSuccess, data: surveyListStats = [] } = useGetStatsQuery({
    initiativeId,
    ...getUTCDateRange(dateRange),
  });

  const userStatsQuery = useGetUserStatsQuery({
    initiativeId,
    ...getUTCDateRange(dateRange),
  })

  const [isEditingSurveys, setIsEditingSurveys] = useState<boolean>(false);

  const [period, setPeriod] = useState(TIME_RANGE_OPTIONS[0].code);

  const handleReload = async () => {
    dispatch(initiativeApi.util.invalidateTags(['stats']))
  }

  useEffect(() => {
    dispatch(loadInitiativeById(initiativeId))
  }, [dispatch, initiativeId])

  const handleDownload = async () => {
    if (initiativeState?.data?._id) {
      G17Client.downloadManagerInitiativeStats(initiativeState.data._id, { ...getUTCDateRange(dateRange) })
    }
  }

  const filteredRows: SurveyStats[] = useMemo(() => {
    return !isSuccess ? [] : surveyListStats.reduce((a, c) => {
      if (c.surveys) {
        a.push(...c.surveys.filter(s => isCompleteFilter ? s.completedDate : !s.completedDate))
      }
      return a;
    }, [] as SurveyStats[]);
  }, [surveyListStats, isSuccess, isCompleteFilter]);

  const users: UserWithDelegation[] = useMemo(() => {
    if (!userStatsQuery.data) {
      return [];
    }

    if (surveyListStats.length === 0 || userFilter !== UserFilter.Permission) {
      return userStatsQuery.data.map((user) => ({
        ...user,
        delegationStats: getUserDelegationStats(user),
      }));
    }

    let defaultStatus: StatusStats = {
      created: 0,
      updated: 0,
      rejected: 0,
      verified: 0,
    };

    const aggregatedSurveyStatus = surveyListStats.reduce(
      (acc, surveyListStat): StatusStats => ({
        created: acc.created + surveyListStat.status.created,
        updated: acc.updated + surveyListStat.status.updated,
        rejected: acc.rejected + surveyListStat.status.rejected,
        verified: acc.verified + surveyListStat.status.verified,
      }),
      defaultStatus
    );

    return userStatsQuery.data.map((user) => {
      let userWithDelegation = { ...user };

      if (InitiativePermissions.canContribute(user, initiativeId)) {
        userWithDelegation = {
          ...userWithDelegation,
          contributor: {
            created: aggregatedSurveyStatus.created,
            updated: aggregatedSurveyStatus.updated,
            verified: aggregatedSurveyStatus.verified,
            rejected: aggregatedSurveyStatus.rejected,
          },
        };
      }
      if (InitiativePermissions.canVerify(user, initiativeId)) {
        userWithDelegation = {
          ...userWithDelegation,
          verifier: {
            created: aggregatedSurveyStatus.created,
            updated: aggregatedSurveyStatus.updated,
            verified: aggregatedSurveyStatus.verified,
            rejected: aggregatedSurveyStatus.rejected,
          },
        };
      }

      return {
        ...userWithDelegation,
        delegationStats: getUserDelegationStats(userWithDelegation),
      };
    });
  }, [userStatsQuery.data, surveyListStats, userFilter, initiativeId]);

  if (!initiativeState.loaded || isLoading || userStatsQuery.isLoading) {
    return <Loader />;
  }

  const initiative = initiativeState.data;

  const initiativeStatuses = surveyListStats.find(i => i._id === initiativeId)?.status;
  const progress = initiativeStatuses ? toPercentageProgress(initiativeStatuses) : undefined;

  const initiatives = surveyListStats.filter(i => i.parentId === initiativeId).sort((a, b) => naturalSort(a.name, b.name));

  const columns: TableColumn<SurveyStats>[] = [
    {
      Header: 'Type',
      accessor: (row) => getPeriodName(row.period, false),
    },
    {
      Header: 'Up to',
      accessor: (row) => formatDate(row.effectiveDate, DATE.MONTH_YEAR_SHORT, true),
      sortType: (a, b) => naturalSort(a.original.effectiveDate, b.original.effectiveDate),
      id: 'date',
    },
    {
      cellProps: {
        style: {
          maxWidth: 250
        }
      },
      Header: 'Title',
      accessor: (row) => {
        return <Link to={`${url}/overview/${row._id}`}>
          <span className={'text-truncate'} style={{ display: 'block' }}>
            {row.name ?? row.initiativeName ?? '-'}
          </span>
        </Link>;
      },
    },
    {
      Header: 'Updated',
      accessor: (row: SurveyStats) => formatDate(row.lastUpdated, DATE.HUMANIZE),
      sortType: (a, b) => naturalSort(a.original.lastUpdated, b.original.lastUpdated),
    },
    {
      Header: () => <i className='fa fa-user-clock text-secondary mr-2' />,
      accessor: 'status.created',
      cellProps: { style: { width: 20, } }
    },
    {
      Header: () => <i className='fa fa-user-edit text-primary mr-2' />,
      accessor: 'status.updated',
      cellProps: { style: { width: 20, } }
    },
    {
      Header: () => <i className='fa fa-user-times text-danger mr-2' />,
      accessor: 'status.rejected',
      cellProps: { style: { width: 20, } }
    },
    {
      Header: () => <i className='fa fa-user-check text-success mr-2' />,
      accessor: 'status.verified',
      cellProps: { style: { width: 20, } }
    },
    {
      id: 'mark',
      Header: () =>
        isUserAdmin ? (
          <Button onClick={() => setIsEditingSurveys(true)} color={'link'} className='btn-xs'>
            Bulk {isCompleteFilter ? 'incomplete' : 'complete'}
          </Button>
        ) : null,
      cellProps: {
        style: {
          width: 100,
        }
      },
      accessor: (row: SurveyStats) => (
        <CompleteButton
          color='link'
          className='btn-xs text-muted'
          isUserAdmin={isUserAdmin}
          survey={row}
          handleReload={handleReload}
        />
      ),
      disableSortBy: true,
    },
    {
      id: 'message',
      cellProps: {
        style: {
          width: 100,
        }
      },
      Header: () => <Button size='sm' color={'link'} className={'btn-xs'} onClick={() => setShowBulkSurveysMessage(true)}>
        Bulk Message
      </Button>,
      disableSortBy: true,
      accessor: (row: SurveyStats) => {
        return <Button size={'sm'} color={'link'} className={'btn-xs text-muted'} onClick={() => setShowMessageModal(row._id)}>
          <i className={'fa fas-lock mr-2'} />Message
        </Button>;
      },
    },
  ];

  if (limitReportingLevels === 1 && isSingleOrganisation) {
    const redirectUrl = generateUrl(ROUTES.ADMIN_SETTINGS, {
      initiativeId: initiativeId,
    });
    return <Redirect to={redirectUrl} />;
  }

  return (
    <div className='admin-dashboard'>
      <Dashboard>
        <DashboardRow>
          <AdminBreadcrumbs breadcrumbs={[{ label: 'Admin Dashboard' }]} initiativeId={initiativeId} />
        </DashboardRow>

        <DashboardSectionTitle
          title={<>Admin dashboard: Overview<Button color='link' className='ml-2'><i className='fas fa-file-excel' onClick={() => handleDownload()}></i></Button> </>}
          buttons={[
            <PeriodDropdown
              period={period}
              onChangePeriod={(value) => setPeriod(value)}
              dateRange={dateRange}
              onChangeDateRange={(range) => setDateRange(range)}
              timeRangeOptions={TIME_RANGE_OPTIONS}
            />
          ]}
        />

        {progress ? <ProgressRow title={initiative.name} progress={progress} style={{ height: '2rem' }} /> : null}

        <DashboardSection padding={1}>
          <div className={'sub-reporting-level-list'}>
            {initiatives.map(({ _id, name, status }) => {
              const counts = toPercentageProgress(status)

              return <div key={_id} className={'mb-4'}>
                <h6>{name}</h6>
                <Progress className={'w-100'} style={{ height: '1rem' }} multi>
                  <Progress className={'background-ThemeAccentExtradark'} bar value={counts.updated}>{counts.updated}%</Progress>
                  <Progress className={'background-ThemeDangerMedium'} bar
                    value={counts.rejected}>{counts.rejected}%</Progress>
                  <Progress className={'background-ThemeSuccessMedium'} bar
                    value={counts.verified}>{counts.verified}%</Progress>
                  <Progress className={'background-chateauGrey'} bar
                    value={counts.created}>{counts.created}%</Progress>
                </Progress>
              </div>
            })}
          </div>
        </DashboardSection>

        <DashboardSectionTitle
          title='Surveys'
          buttons={[
            <Button className='py-1 mr-3' onClick={() => history.push(generateUrl(ROUTES.BULK_IMPORTING, { initiativeId }))}>
              <span className='lh-base'><i className='fa-light fa-file-import mr-2 fs-5' />Bulk import data</span>
            </Button>,
            <Button className='py-1' onClick={() => history.push(generateUrl(ROUTES.SURVEY_TEMPLATES, { initiativeId }))}>
              <span className='lh-base'><i className='fa-light fa-layer-group mr-2 fs-5' />Survey templates</span>
            </Button>,
          ]}
        />

        <DashboardSection>
          <Toggle
            className={{ form: 'd-flex align-items-center pl-0 mb-3 justify-content-end' }}
            checked={isCompleteFilter}
            onChange={() => (isCompleteFilter ? setFilter(Filter.Pending) : setFilter(Filter.Completed))}
            label='Completed surveys'
          />
          {isLoading || isFetching ? (
            <Loader relative />
          ) : (
            <Table
              defaultSortIcon={null}
              sortBy={[{ id: 'date', desc: false }]}
              showRowCount={false}
              responsive={false}
              pageSize={10}
              columns={columns}
              data={filteredRows}
              noData={<BasicAlert type='secondary'>There are no {isCompleteFilter ? 'Completed' : 'In Progress'} surveys.</BasicAlert>}
            />
          )}
        </DashboardSection>

        <DashboardSectionTitle
          title='Users'
          buttons={users.length > 0 ? [
            <Button className='py-1' onClick={() => setShowBulkUsersMessage(true)}>
              <span className='lh-base'><i className='fa-light fa-envelope mr-2 fs-5' />Bulk message users</span>
            </Button>,
          ] : []}
        />

        <DashboardSection>
          <Toggle
            className={{ form: 'd-flex align-items-center pl-0 mb-3 justify-content-end' }}
            checked={isPermissionFilter}
            onChange={() => (isPermissionFilter ? setUserFilter(UserFilter.Explicit) : setUserFilter(UserFilter.Permission))}
            label='View all with permissions'
          />
          <AdminUserTable
            onUserClick={(user) => {
              history.push(`${url}/users/${user._id}`);
            }}
            initiativeId={initiativeId}
            highlightUserId={userId}
            users={users}
            error={userStatsQuery.error}
          />
        </DashboardSection>
        <DashboardSectionTitle
          title={QUESTION.CAPITALIZED_PLURAL}
          buttons={[
            <Button className='py-1' onClick={() => history.push(`${url}/questions`)}>
              <span className='lh-base'>View all {QUESTION.PLURAL}</span>
            </Button>,
          ]}
        />
        <DashboardSection>
          <QuestionManagement pageSize={PAGE_SIZE} initiativeId={initiativeId} />
        </DashboardSection>
      </Dashboard>
      {
        showMessageModal ? (
          <SurveyMessageModal surveyId={showMessageModal} toggle={() => setShowMessageModal(undefined)} />
        ) : null
      }
      {
        isEditingSurveys && (
          <SurveysEditing
            surveys={filteredRows}
            action={isCompleteFilter ? Action.Incomplete : Action.Complete}
            handleClose={() => setIsEditingSurveys(false)}
            handleReload={handleReload}
          />
        )
      }
      {
        showBulkSurveysMessage ? (
          <BulkSurveysMessageModal surveys={filteredRows} toggle={() => setShowBulkSurveysMessage(false)} />
        ) : null
      }
      {showBulkUsersMessage ? (
        <BulkUsersMessageModal users={users} toggle={() => setShowBulkUsersMessage(false)} />
      ) : null}
    </div >
  );
};
